import { useForm } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { FieldError } from '@/components/field-error'
import { PageHeader } from '@/components/page'
import { ImageUpload } from '@/components/image-upload'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { fromLocalInput, toLocalInput, utcLabel } from '@/lib/datetime'
import {
  bannerSchema,
  usePromoBanner,
  useSavePromoBanner,
  type Banner,
  type BannerInput,
} from '@/features/promo-banner/api'

export function PromoBannerPage() {
  const { data, isPending, isError, error, refetch } = usePromoBanner()

  if (isPending) {
    return (
      <>
        <PageHeader title="Promo Banner" />
        <Card>
          <Skeleton className="h-37.5 w-full rounded-xl" />
        </Card>
      </>
    )
  }

  if (isError) {
    return (
      <>
        <PageHeader title="Promo Banner" />
        <Card>
          <CardTitle>Could not load the banner</CardTitle>
          <CardDescription className="mt-2">{(error as Error).message}</CardDescription>
          <Button variant="secondary" size="sm" className="mt-4" onClick={() => void refetch()}>
            Try again
          </Button>
        </Card>
      </>
    )
  }

  return <BannerEditor banner={data} />
}

/**
 * Split from the page so the form is constructed *after* the row has loaded —
 * defaultValues can then come straight from it, with no effect syncing the two
 * and no first render where the Select has nothing to show.
 */
function BannerEditor({ banner }: { banner: Banner }) {
  const save = useSavePromoBanner()

  const form = useForm<BannerInput>({
    resolver: standardSchemaResolver(bannerSchema),
    defaultValues: {
      image_url: banner.image_url,
      link_url: banner.link_url ?? '',
      state: banner.state,
      starts_at: banner.starts_at ?? null,
      ends_at: banner.ends_at ?? null,
    },
  })

  // Watched so the hero preview tracks typing, as it does in the prototype.
  const preview = form.watch()
  const startsAt = form.watch('starts_at')
  const endsAt = form.watch('ends_at')

  function setWindow(field: 'starts_at' | 'ends_at', value: string) {
    form.setValue(field, fromLocalInput(value), { shouldDirty: true, shouldValidate: true })
  }

  return (
    <>
      <PageHeader title="Promo Banner" />

      <Card className="mb-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <CardTitle>Current banner</CardTitle>
          <Badge variant={preview.state === 'live' ? 'success' : 'default'}>
            {preview.state === 'live' ? 'Live' : 'Off'}
          </Badge>
        </div>

        <HeroPreview imageUrl={preview.image_url} dimmed={preview.state !== 'live'} />

        <CardDescription className="mt-3">
          Preview of what members see in the homepage hero. Off is shown dimmed — it is hidden
          entirely on the consumer site.
        </CardDescription>
      </Card>

      <Card>
        <CardTitle>Edit banner</CardTitle>
        <CardDescription className="mt-1">
          One banner at a time, upserted. Shown on the consumer site only when{' '}
          <b>State is Live AND the current time falls within the window below</b> (leave the
          window empty to go by State alone).
        </CardDescription>

        <form
          className="mt-5 space-y-5"
          onSubmit={form.handleSubmit((values) =>
            save.mutate(values, {
              // Re-baseline on what the server stored, so the form goes clean,
              // Save and Cancel disable, and Cancel now reverts to the saved
              // row rather than the one loaded on mount.
              onSuccess: (saved) =>
                form.reset({
                  image_url: saved.image_url,
                  link_url: saved.link_url ?? '',
                  state: saved.state,
                  starts_at: saved.starts_at ?? null,
                  ends_at: saved.ends_at ?? null,
                }),
            }),
          )}
        >
          <div>
            <ImageUpload
              value={form.watch('image_url')}
              onChange={(url) =>
                form.setValue('image_url', url, { shouldDirty: true, shouldValidate: true })
              }
              purpose="banner"
              label="Banner image"
              // Matches the design spec (2816×300) so what the operator sees while
              // cropping/uploading is the same very-wide-and-short shape the
              // consumer site actually renders — a 3:1 preview here previously let
              // through images that then stretched way too tall on the homepage.
              aspectClassName="aspect-[2816/300] max-w-md"
            />
            <FieldError message={form.formState.errors.image_url?.message} />
            <p className="mt-1 text-xs text-faint">
              Exactly 2816×300 (design spec); shown full-width in the homepage hero.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="link_url">URL</Label>
            <Input
              id="link_url"
              placeholder="/promotions/launch"
              {...form.register('link_url')}
            />
            <FieldError message={form.formState.errors.link_url?.message} />
            <p className="text-xs text-faint">
              Where tapping the banner takes a member. A site-relative path like{' '}
              <code>/promotions/launch</code>, or a full <code>https://</code> URL for an
              external campaign.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="state">State</Label>
            <Select
              value={form.watch('state')}
              onValueChange={(value) =>
                form.setValue('state', value as BannerInput['state'], { shouldDirty: true })
              }
            >
              <SelectTrigger id="state" className="max-w-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="live">Live on homepage hero</SelectItem>
                <SelectItem value="off">Off (hidden)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="banner-starts">Starts (optional)</Label>
              <Input
                id="banner-starts"
                type="datetime-local"
                value={toLocalInput(startsAt)}
                onChange={(event) => setWindow('starts_at', event.target.value)}
              />
              <p className="text-xs text-faint">
                {startsAt ? utcLabel(startsAt) : 'Empty — live the moment State is set to Live.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="banner-ends">Ends (optional)</Label>
              <Input
                id="banner-ends"
                type="datetime-local"
                value={toLocalInput(endsAt)}
                onChange={(event) => setWindow('ends_at', event.target.value)}
              />
              <p className="text-xs text-faint">{endsAt ? utcLabel(endsAt) : 'Empty — no expiry.'}</p>
              <FieldError message={form.formState.errors.ends_at?.message} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={save.isPending || !form.formState.isDirty}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={save.isPending || !form.formState.isDirty}
              onClick={() => form.reset()}
            >
              Cancel
            </Button>

            {save.isError && (
              <span className="text-xs text-destructive">
                {(save.error as Error).message}
              </span>
            )}
            {save.isSuccess && !form.formState.isDirty && (
              <span className="text-xs text-muted-foreground">
                Saved{save.data?.updated_by ? ` by ${save.data.updated_by}` : ''}.
              </span>
            )}
          </div>
        </form>
      </Card>
    </>
  )
}

/**
 * Mirrors the consumer homepage hero closely enough to judge the crop against.
 * Fixed 2816:300 aspect box + object-cover, matching the design spec and
 * PromoBanner.vue's own max-height cap — so an operator sees here exactly how
 * an off-spec image will actually get cropped, instead of a free-height
 * preview that hides the problem until it ships.
 */
function HeroPreview({ imageUrl, dimmed }: { imageUrl: string; dimmed: boolean }) {
  return (
    <div
      className={cn(
        'relative aspect-[2816/300] w-full overflow-hidden rounded-xl transition-opacity',
        dimmed && 'opacity-45',
      )}
      style={{ background: 'linear-gradient(120deg,#0b1e5b,#3861fb 60%,#7c9bff)' }}
    >
      {imageUrl && (
        <img src={imageUrl} alt="" className="absolute inset-0 size-full object-cover" />
      )}
    </div>
  )
}
